import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Download, Share2, Pencil, Trash2, FileText, Copy, Check, Lock } from "lucide-react";
import {
  listClientFiles,
  uploadClientFile,
  renameClientFile,
  deleteClientFile,
  shareClientFile,
  downloadFileUrl,
  publicShareUrl,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";

function formatSize(bytes) {
  if (!bytes) return "0 B";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ClientFiles({ clientId, category, title, description }) {
  const { isAdmin, canEdit } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [renameDialog, setRenameDialog] = useState(null); // {file, name}
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [shareDialog, setShareDialog] = useState(null); // {file, url, expires_at, copied}
  const inputRef = useRef(null);

  const canRename = category === "documento";
  const canDelete = category === "documento" && isAdmin;

  const load = useCallback(async () => {
    try {
      setFiles(await listClientFiles(clientId, category));
    } catch {
      toast.error("Erro ao carregar arquivos");
    } finally {
      setLoading(false);
    }
  }, [clientId, category]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      for (const file of fileList) {
        await uploadClientFile(clientId, category, file, (p) => setUploadProgress(p));
      }
      toast.success(fileList.length > 1 ? "Arquivos enviados" : "Arquivo enviado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!canEdit) return;
    doUpload(Array.from(e.dataTransfer.files || []));
  };

  const doRename = async () => {
    if (!renameDialog) return;
    const name = renameDialog.name.trim();
    if (!name) { toast.error("Nome não pode ser vazio"); return; }
    try {
      await renameClientFile(clientId, renameDialog.file.id, name);
      toast.success("Nome atualizado");
      setRenameDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renomear");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteClientFile(clientId, confirmDelete.id);
      toast.success("Arquivo excluído");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const doShare = async (file) => {
    try {
      const r = await shareClientFile(clientId, file.id);
      setShareDialog({ file, url: publicShareUrl(r.token), expires_at: r.expires_at, copied: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar link");
    }
  };

  const doDownload = (file) => {
    const url = downloadFileUrl(clientId, file.id);
    // Programmatic download using cookie auth (backend serves original bytes)
    const a = document.createElement("a");
    a.href = url;
    a.download = file.original_name;
    a.click();
  };

  const copyLink = async () => {
    if (!shareDialog?.url) return;
    try {
      await navigator.clipboard.writeText(shareDialog.url);
      setShareDialog((s) => ({ ...s, copied: true }));
      toast.success("Link copiado");
    } catch { toast.error("Erro ao copiar"); }
  };

  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 sm:p-6" data-testid={`client-files-${category}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-[#0F2942] dark:text-white">{title}</h2>
          {description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>}
        </div>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => doUpload(Array.from(e.target.files || []))}
              data-testid={`file-input-${category}`}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`upload-button-${category}`}
              className="bg-[#0F2942] hover:bg-[#173E63] text-white flex-shrink-0"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? `Enviando ${uploadProgress}%` : "Anexar"}
            </Button>
          </>
        )}
      </div>

      {canEdit && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center text-xs text-slate-400 dark:text-slate-500 mb-4"
          data-testid={`drop-zone-${category}`}
        >
          Arraste arquivos aqui ou clique em Anexar. Limite de 50 MB por arquivo.
          {category === "contrato_social" && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1">
              <Lock className="h-3 w-3" /> Contratos sociais são preservados permanentemente e não podem ser excluídos.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-400">Carregando...</div>
      ) : files.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400 dark:text-slate-500">
          Nenhum arquivo anexado.
        </div>
      ) : (
        <ul className="space-y-2" data-testid={`files-list-${category}`}>
          {files.map((f) => (
            <li
              key={f.id}
              data-testid={`file-row-${f.id}`}
              className="flex items-center gap-3 p-3 border border-slate-100 dark:border-slate-800 rounded-lg bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors"
            >
              <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-[#0F2942] text-white flex items-center justify-center">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-[#0F172A] dark:text-slate-100 truncate">
                  {f.custom_name}
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                  {formatSize(f.file_size)} • enviado em {formatDateTime(f.uploaded_at)} por {f.uploaded_by_name || "—"}
                </p>
                {f.custom_name !== f.original_name && (
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate italic">
                    Original: {f.original_name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doDownload(f)}
                  data-testid={`download-file-${f.id}`}
                  title="Baixar arquivo original"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doShare(f)}
                  data-testid={`share-file-${f.id}`}
                  title="Gerar link de compartilhamento"
                >
                  <Share2 className="h-3.5 w-3.5" />
                </Button>
                {canRename && canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRenameDialog({ file: f, name: f.custom_name })}
                    data-testid={`rename-file-${f.id}`}
                    title="Renomear"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDelete(f)}
                    data-testid={`delete-file-${f.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Excluir"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Rename Dialog */}
      <Dialog open={!!renameDialog} onOpenChange={(o) => !o && setRenameDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Renomear documento</DialogTitle>
            <DialogDescription>
              O nome exibido pode ser editado. O arquivo original ({renameDialog?.file?.original_name}) permanece inalterado no download.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameDialog?.name || ""}
            onChange={(e) => setRenameDialog((s) => ({ ...s, name: e.target.value }))}
            placeholder="Ex: RG — João da Silva"
            data-testid="rename-file-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog(null)}>Cancelar</Button>
            <Button onClick={doRename} data-testid="rename-file-submit" className="bg-[#0F2942] hover:bg-[#173E63] text-white">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!shareDialog} onOpenChange={(o) => !o && setShareDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Compartilhar arquivo</DialogTitle>
            <DialogDescription>
              Link temporário para <b>{shareDialog?.file?.custom_name}</b>. Válido por 48 horas. Qualquer pessoa com o link pode baixar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <Input value={shareDialog?.url || ""} readOnly onFocus={(e) => e.target.select()} data-testid="share-url-input" />
            <Button onClick={copyLink} data-testid="copy-share-link" className="flex-shrink-0">
              {shareDialog?.copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {shareDialog?.copied ? "Copiado" : "Copiar"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Expira em: {formatDateTime(shareDialog?.expires_at)}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialog(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir arquivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <b>{confirmDelete?.custom_name}</b>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-file" className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
